import { Box, Grid, MenuItem, Select, Typography } from '@mui/material';
import { Base } from 'components/Containers/BaseContainer';
import { FlexBox } from 'components/Containers/FlexBox';
import { ThemePalette } from 'mui.theme';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { truncateTo2dp } from 'utils/helpers/general';
import { assetMaintainenaceComparisonViewOptions } from '../Constants/constants';
import { filterObjectByKeys } from '../utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
type AssetData = Record<
  string,
  Record<string, { sumTotalCost: number; count: number; sumDownTime: number }>
>;

type FormattedData = Array<{
  name: string;
  sumTotalCost: number;
  count: number;
  sumDownTime: number;
}>;

// moved data formting out of component
const formatAssetData = (assetData: AssetData): FormattedData => {
  return Object.values(assetData)
    .flatMap((category) =>
      Object.entries(category)
        .filter(([name]) => ['PM', 'CM'].includes(name))
        .map(([name, value]) => ({ name, ...value }))
    )
    .reduce<FormattedData>((acc, item) => {
      const existing = acc.find((el) => el.name === item.name);

      if (existing) {
        existing.sumTotalCost += item.sumTotalCost || 0;
        existing.count += item.count || 0;
        existing.sumDownTime += (item.sumDownTime || 0) / (1000 * 60 * 60);
      } else {
        acc.push({
          name: item.name,
          sumTotalCost: item.sumTotalCost || 0,
          count: item.count || 0,
          sumDownTime: (item.sumDownTime || 0) / (1000 * 60 * 60),
        });
      }

      return acc;
    }, []);
};

// made a reusable tooltip component for diff views
const CustomTooltip = ({ active, payload, dataKey, name }: any) => {
  if (active && payload && payload.length) {
    let value;
    switch (dataKey) {
      case 'sumTotalCost':
        value = `$${truncateTo2dp(payload[0].payload[dataKey])}`;
        break;
      case 'count':
        value = payload[0].payload[dataKey];
        break;
      case 'sumDownTime':
        value = `${payload[0].payload[dataKey]} hrs`;
        break;
      default:
        value = payload[0].payload[dataKey];
    }

    return (
      <div
        className="custom-tooltip"
        style={{ background: '#ffffff', padding: 10, border: '1px solid #ccc' }}
      >
        <p>{`Category: ${payload[0].payload.name}`}</p>
        <p>{`${name}: ${value}`}</p>
      </div>
    );
  }
  return null;
};

// made a resuable chart component for differnt views
const ChartComponent = ({
  data,
  dataKey,
  fill,
  name,
  enableDarkTheme,
}: {
  data: any;
  dataKey: string;
  fill: string;
  name: string;
  enableDarkTheme: boolean;
}) => (
  <FlexBox
    sx={{
      maxWidth: 'inherit',
      height: '300px',
      paddingTop: '20px',
      marginRight: '10px',
      paddingBottom: '10px',
    }}
  >
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} barSize={50}>
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip
          cursor={{
            fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC',
          }}
          content={<CustomTooltip dataKey={dataKey} name={name} />}
        />
        <Legend />
        <Bar dataKey={dataKey} fill={fill} name={name} />
      </BarChart>
    </ResponsiveContainer>
  </FlexBox>
);

const MaintenanceComparison = ({
  enableDarkTheme,
  data,
  selectedCell,
}: {
  enableDarkTheme: boolean;
  data: any;
  selectedCell: string[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [selectedView, setSelectedView] = useState(
    assetMaintainenaceComparisonViewOptions?.[0]?.value
  );

  // added memoization to avoid unneccessary calc
  const assetData = useMemo(() => {
    const temp = filterObjectByKeys(data.maintenanceCounter, selectedCell);
    return temp || {};
  }, [data, selectedCell]);

  const chartData = useMemo(() => {
    const temp = formatAssetData(assetData);
    return temp;
  }, [assetData]);

  return (
    <Base enableDarkTheme={enableDarkTheme}>
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.toolBarBackground
            : palette.toolBarBackground,
          padding: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Typography
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('selected_view')}
          </Typography>
          <Select
            labelId="parts-recommendation-selected-asset"
            value={selectedView}
            onChange={(e: any) => {
              setSelectedView(e.target.value);
            }}
            sx={{
              height: '40px',
              fontSize: '14px',
              width: '200px',
              backgroundColor: enableDarkTheme
                ? ThemePalette.dark.cartesianGrid
                : palette.boxBackground,
              color: palette.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.textSecondary,
              },
              '& fieldset': {
                borderColor: palette.textPrimary,
              },
              ...(enableDarkTheme && {
                '&.MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: palette.cardBorderColor,
                  },
                },
              }),
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                  color: palette.textPrimary,
                  fontSize: '14px',
                },
              },
            }}
          >
            {assetMaintainenaceComparisonViewOptions.map((option, index) => (
              <MenuItem
                key={option?.value || index}
                value={option?.value}
                sx={{
                  fontSize: '14px',
                }}
              >
                {option?.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            {/** Maintenance cost */}
            {selectedView === 'maintenance_cost' && (
              <ChartComponent
                data={chartData}
                dataKey="sumTotalCost"
                fill="#feb019"
                name="Maintenance Cost"
                enableDarkTheme={enableDarkTheme}
              />
            )}

            {/** Maintenance count */}
            {selectedView === 'maintenance_count' && (
              <ChartComponent
                data={chartData}
                dataKey="count"
                fill="#008ffb"
                name="Maintenance Count"
                enableDarkTheme={enableDarkTheme}
              />
            )}

            {/** Downtime Hours */}
            {selectedView === 'downtime_hours' && (
              <ChartComponent
                data={chartData}
                dataKey="sumDownTime"
                fill="#775dd0"
                name="Down Time Hours"
                enableDarkTheme={enableDarkTheme}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Base>
  );
};

export default MaintenanceComparison;
